import { configureStore } from '@reduxjs/toolkit'
import { getLocalStorageValue, removeLocalStorageValue } from './localstorage'
import { dataFetcher } from './datafetcher'
import { setRemoteUnauthorizedHandler } from './request'
import {
    asyncConfigureSensors,
    asyncScanSensors,
    asyncGetSensorsData,
    asyncPostPwr,
    isNoData,
    selectedMetricsKey,
    sensorsSlice } from './sensors'
import {
    loginSlice,
    asyncDoLogin,
    asyncRegisterUser,
    loginTokenKey,
    setToken,
    sessionExpired } from './login'

import { activitiesSlice, asyncReqActivities } from './activities'
import { workoutsSlice, asyncReqWorkouts, asyncCreateWorkout, asyncSaveLiveActivity } from './workouts'
import { asyncReqUserGeneralInfo, userinfoSlice } from './userinfo'
import { usermeasuresSlice } from './usermeasures'
import { asyncChangePassword, passwordSlice } from './password'
import { asyncGetSensorsStatus, asyncGetUsbDevices, asyncSetUsbDevice, connectivitySlice } from './connectivity'
import { asyncCheckDaemonCompatibility, assertDaemonCompatible as assertDaemonCompatibleWith } from './daemonCompatibility'
import { PENDING_WORKOUT_KEY } from '../workoutCapture'
    
const reducer = {
    sensors: sensorsSlice.reducer,
    login: loginSlice.reducer,
    userinfo: userinfoSlice.reducer,
    usermeasures: usermeasuresSlice.reducer,
    activities: activitiesSlice.reducer,
    workouts: workoutsSlice.reducer,
    password: passwordSlice.reducer,
    connectivity: connectivitySlice.reducer
    //TODO: add mode: training vs. browsing
}

export const store = configureStore({ reducer })

// On a remote 401 (expired/revoked JWT) clear only the session: unlike
// logoutUser() this deliberately leaves PENDING_WORKOUT_KEY and the player
// capture untouched so an in-progress ride survives and resolves on exit.
setRemoteUnauthorizedHandler(() => {
    removeLocalStorageValue(loginTokenKey)
    store.dispatch(sessionExpired())
})

// unwrap() rejects on failure so the resilient poller can distinguish a dead
// daemon (rejection → back off and keep re-trying) from a healthy one.
dataFetcher.init(() => store.dispatch(asyncGetSensorsData()).unwrap())

const RECONFIGURE_AFTER_EMPTY_POLLS = 3
const RECONFIGURE_COOLDOWN_MS = 10000
let emptyConfiguredPolls = 0
let lastAutoReconfigureAt = Date.now()

// The sensor channel assignment lives only in the daemon's memory and is lost
// when it restarts. Once a "configured" session keeps polling empty data (the
// daemon is alive but has no channels), re-assert the saved selection so the
// app recovers without a manual scan/configure. A fresh daemon closes nothing,
// so this completes in milliseconds on a restart (not the ~12 s warm-configure).
store.subscribe(() => {
    const sensors = store.getState().sensors
    if (!sensors || sensors.mode !== 'configured') {
        emptyConfiguredPolls = 0
        return
    }

    if (isNoData(sensors)) {
        emptyConfiguredPolls++
        if (emptyConfiguredPolls >= RECONFIGURE_AFTER_EMPTY_POLLS) {
            emptyConfiguredPolls = 0
            const now = Date.now()
            if (now - lastAutoReconfigureAt >= RECONFIGURE_COOLDOWN_MS) {
                lastAutoReconfigureAt = now
                const selection = sensors.selectedMetrics
                if (selection && selection.length) {
                    store.dispatch(asyncConfigureSensors(selection))
                }
            }
        }
    } else {
        emptyConfiguredPolls = 0
    }
})

// The /version verdict is otherwise only refreshed at view mount, so the
// outdated-daemon banner would outlive a daemon upgrade: status polling and
// the manual «Проверить» fetch /status alone. Re-grading on every successful
// status response (lastChecked advancing, or returning to reachable for
// status-less bodies) clears the banner once the updated daemon answers —
// no reload needed.
let prevReachable = store.getState().connectivity.reachable
let prevLastChecked = store.getState().connectivity.lastChecked
store.subscribe(() => {
    const { reachable, lastChecked } = store.getState().connectivity
    const refreshed = reachable === true
        && (reachable !== prevReachable || lastChecked !== prevLastChecked)
    prevReachable = reachable
    prevLastChecked = lastChecked
    if (refreshed)
        store.dispatch(asyncCheckDaemonCompatibility())
})

export function fetchSensors() {
    store.dispatch(asyncScanSensors())
}

export function fetchStatus() {
    store.dispatch(asyncGetSensorsStatus())
}

export function checkDaemonCompatibility() {
    return store.dispatch(asyncCheckDaemonCompatibility())
}

export async function assertDaemonCompatible() {
    return assertDaemonCompatibleWith(store.dispatch)
}

export function fetchUsbDevices() {
    store.dispatch(asyncGetUsbDevices())
}

export function setUsbDevice(id) {
    return store.dispatch(asyncSetUsbDevice(id))
}

let statusPollTimer = null

export function startStatusPolling(ms) {
    stopStatusPolling()
    fetchStatus()
    statusPollTimer = setInterval(() => {
        store.dispatch(asyncGetSensorsStatus())
    }, ms || 5000)
}

export function stopStatusPolling() {
    if (statusPollTimer) {
        clearInterval(statusPollTimer)
        statusPollTimer = null
    }
}

export function configureSensors(metrics) {
    store.dispatch(asyncConfigureSensors(metrics))
}

export function postPwr(data) {
    return store.dispatch(asyncPostPwr(data))
}

export function startWork() {
    const selectedMetrics = getLocalStorageValue(selectedMetricsKey)
    if (selectedMetrics) {
        configureSensors(selectedMetrics)
    } else {
        fetchSensors()
    }
}

export function checkAuthorizationStatus() {
    const loginToken = getLocalStorageValue(loginTokenKey)
    if (loginToken) {
        const { expiration, token } = loginToken
        if (expiration > Date.now()) {
            store.dispatch(setToken(token))
        } else {
            removeLocalStorageValue(loginTokenKey)
        }
    }
}

export function logoutUser() {
    removeLocalStorageValue(loginTokenKey)
    removeLocalStorageValue(PENDING_WORKOUT_KEY)
    store.dispatch(setToken(''))
}

export function doLogin(data) {
    store.dispatch(asyncDoLogin(data))
}

export function getLoginToken() {
    const state = store.getState()
    if (state.login)
        return state.login.token
    else
        return ''
}

export function registerUser(data) {
    store.dispatch(asyncRegisterUser(data))
}

export function changePassword(data) {
    store.dispatch(asyncChangePassword(data))
}

export function requestMyActivities(data) {
    store.dispatch(asyncReqActivities(data))
}

export function requestWorkouts() {
    store.dispatch(asyncReqWorkouts())
}

export function createWorkout(data) {
    return store.dispatch(asyncCreateWorkout(data))
}

export function saveLiveActivity(data) {
    return store.dispatch(asyncSaveLiveActivity(data))
}

export function requestUserInfoGeneral(data) {
    const userinfo = store.getState().userinfo
    if ((!userinfo.info || !userinfo.info.first_name) && userinfo.infoIsLoading === false) {
        store.dispatch(asyncReqUserGeneralInfo(data))
    }
}
